"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.concentrationLimitPointerMethods = void 0;
const common_1 = require("@finley-technologies/common");
/**
 * This pointer method basically is trying to simulate if Okinus has distinct loan products,
 * since they don't provide loan tapes in that manner.
 * Basically filtering on "Merchandise" product_type and checking against largest retail_dealer
 * against the value limit
 */
const topSellerRetailerOfNonHvacContracts = (params) => {
    const { concentrationReceivables, valueConfig, totalConcentrationExposure, } = params;
    // Just return early if no receivables are passed in
    if (concentrationReceivables.length === 0) {
        return {
            kickouts: {},
            calculations: {
                concentrationLimit: valueConfig.limit,
                concentrationAmount: totalConcentrationExposure,
                concentration: 0,
            },
        };
    }
    // *** Set up hardcoded values ***
    const baseFieldProductType = 'product_type';
    const baseFieldRetailerDealerId = 'retailer_dealer_id';
    // Organize exposure map by "retailer_dealer_id" groups for "Merchandise" product type receivables.
    // Also aggregate total exposure by "Merchandise" receivables, as that's the comparison point
    let totalMerchandiseEligibleLessKickout = 0;
    const exposureByRetailerDetailIdMap = concentrationReceivables.reduce((exposureMap, receivable) => {
        const { finleyReceivableId, baseFields, concentrationLimitFields, } = receivable;
        const { eligibleExposureLessConcentrationLimitKickouts, } = concentrationLimitFields;
        const productType = baseFields[baseFieldProductType];
        (0, common_1.finleyAssert)(typeof productType === 'string', `okinusBastion1.topSellerRetailerOfNonHvacContracts: incurred non-string base field [type:${typeof productType}] [key:${baseFieldProductType}] [receivableId:${finleyReceivableId}]`);
        // Only care about "Merchandise" product types, so can skip if not
        if (productType !== 'Merchandise') {
            return exposureMap;
        }
        totalMerchandiseEligibleLessKickout += eligibleExposureLessConcentrationLimitKickouts;
        const retailerDealerId = baseFields[baseFieldRetailerDealerId];
        (0, common_1.finleyAssert)(typeof retailerDealerId === 'number', `okinusBastion1.topSellerRetailerOfNonHvacContracts: incurred non-number base field [type:${typeof retailerDealerId}] [key:${baseFieldRetailerDealerId}] [receivableId:${finleyReceivableId}]`);
        if (!(retailerDealerId in exposureMap)) {
            // eslint-disable-next-line no-param-reassign
            exposureMap[retailerDealerId] = {
                eligibleLessKickout: 0,
                receivables: [],
            };
        }
        // eslint-disable-next-line no-param-reassign
        exposureMap[retailerDealerId].eligibleLessKickout += eligibleExposureLessConcentrationLimitKickouts;
        exposureMap[retailerDealerId].receivables.push(receivable);
        return exposureMap;
    }, {});
    // Find target group: largest amount by retailer_dealer_id
    let largestGroupTotalEligibleLessKickout = -1;
    let topReceivables = [];
    for (const [_key, value] of Object.entries(exposureByRetailerDetailIdMap)) {
        const { eligibleLessKickout, receivables } = value;
        if (eligibleLessKickout > largestGroupTotalEligibleLessKickout) {
            largestGroupTotalEligibleLessKickout = eligibleLessKickout;
            topReceivables = receivables;
        }
    }
    // *** Manage kickout calculations ***
    // Assess if any kickouts are needed
    const maxTotalEligibleLessKickout = totalMerchandiseEligibleLessKickout * valueConfig.limit;
    const overageKickoutAmount = Math.max(largestGroupTotalEligibleLessKickout - maxTotalEligibleLessKickout, 0);
    // If needed, track needed kickouts by pro-rating target receivables
    const kickouts = {};
    if (overageKickoutAmount > 0) {
        for (const receivable of topReceivables) {
            const { finleyReceivableId, concentrationLimitFields } = receivable;
            const { eligibleExposureLessConcentrationLimitKickouts, } = concentrationLimitFields;
            const proRatePercent = common_1.utils.divideByOrZero(eligibleExposureLessConcentrationLimitKickouts, largestGroupTotalEligibleLessKickout);
            const proRateReductionAmount = proRatePercent * overageKickoutAmount;
            kickouts[finleyReceivableId] = proRateReductionAmount;
        }
    }
    // *** Manage concentration calculation ***
    const concentrationAmount = largestGroupTotalEligibleLessKickout;
    const concentration = common_1.utils.divideByOrZero(concentrationAmount, totalMerchandiseEligibleLessKickout);
    return {
        kickouts,
        calculations: {
            concentrationLimit: valueConfig.limit,
            concentrationAmount,
            concentration,
        },
    };
};
/**
 * Similar to one above ("topSellerRetailerOfNonHvacContracts"), but comparing against top two groups
 * rather than just one
 */
const topTwoSellerRetailerOfNonHvacContracts = (params) => {
    const { concentrationReceivables, valueConfig, totalConcentrationExposure, } = params;
    // Just return early if no receivables are passed in
    if (concentrationReceivables.length === 0) {
        return {
            kickouts: {},
            calculations: {
                concentrationLimit: valueConfig.limit,
                concentrationAmount: totalConcentrationExposure,
                concentration: 0,
            },
        };
    }
    // *** Set up hardcoded values ***
    const baseFieldProductType = 'product_type';
    const baseFieldRetailerDealerId = 'retailer_dealer_id';
    // Organize exposure map by "retailer_dealer_id" groups for "Merchandise" product type receivables.
    // Also aggregate total exposure by "Merchandise" receivables, as that's the comparison point
    let totalMerchandiseEligibleLessKickout = 0;
    const exposureByRetailerDetailIdMap = concentrationReceivables.reduce((exposureMap, receivable) => {
        const { finleyReceivableId, baseFields, concentrationLimitFields, } = receivable;
        const { eligibleExposureLessConcentrationLimitKickouts, } = concentrationLimitFields;
        const productType = baseFields[baseFieldProductType];
        (0, common_1.finleyAssert)(typeof productType === 'string', `okinusBastion1.topTwoSellerRetailerOfNonHvacContracts: incurred non-string base field [type:${typeof productType}] [key:${baseFieldProductType}] [receivableId:${finleyReceivableId}]`);
        // Only care about "Merchandise" product types, so can skip if not
        if (productType !== 'Merchandise') {
            return exposureMap;
        }
        totalMerchandiseEligibleLessKickout += eligibleExposureLessConcentrationLimitKickouts;
        const retailerDealerId = baseFields[baseFieldRetailerDealerId];
        (0, common_1.finleyAssert)(typeof retailerDealerId === 'number', `okinusBastion1.topTwoSellerRetailerOfNonHvacContracts: incurred non-number base field [type:${typeof retailerDealerId}] [key:${baseFieldRetailerDealerId}] [receivableId:${finleyReceivableId}]`);
        if (!(retailerDealerId in exposureMap)) {
            // eslint-disable-next-line no-param-reassign
            exposureMap[retailerDealerId] = {
                eligibleLessKickout: 0,
                receivables: [],
            };
        }
        // eslint-disable-next-line no-param-reassign
        exposureMap[retailerDealerId].eligibleLessKickout += eligibleExposureLessConcentrationLimitKickouts;
        exposureMap[retailerDealerId].receivables.push(receivable);
        return exposureMap;
    }, {});
    const sortedRetailerGroups = Object.values(exposureByRetailerDetailIdMap).sort((a, b) => b.eligibleLessKickout - a.eligibleLessKickout);
    const firstTwoTopRetailerGroups = sortedRetailerGroups.slice(0, 2);
    // Can return early if none was found
    if (firstTwoTopRetailerGroups.length === 0) {
        return {
            kickouts: {},
            calculations: {
                concentrationLimit: valueConfig.limit,
                concentrationAmount: totalConcentrationExposure,
                concentration: 0,
            },
        };
    }
    let totalTopTwoEligibleLessKickout = 0;
    const topTwoReceivables = [];
    // Already checked length !== 0 above, but technically can only be length of 1 still.
    // Using loop to simply manage both scenarios of length of 1 or 2
    for (const retailGroup of firstTwoTopRetailerGroups) {
        totalTopTwoEligibleLessKickout += retailGroup.eligibleLessKickout;
        for (const receivable of retailGroup.receivables) {
            topTwoReceivables.push(receivable);
        }
    }
    // *** Manage kickout calculations ***
    // Assess if any kickouts are needed
    const maxTotalEligibleLessKickout = totalMerchandiseEligibleLessKickout * valueConfig.limit;
    const overageKickoutAmount = Math.max(totalTopTwoEligibleLessKickout - maxTotalEligibleLessKickout, 0);
    // If needed, track needed kickouts by pro-rating target receivables
    const kickouts = {};
    if (overageKickoutAmount > 0) {
        for (const receivable of topTwoReceivables) {
            const { finleyReceivableId, concentrationLimitFields } = receivable;
            const { eligibleExposureLessConcentrationLimitKickouts, } = concentrationLimitFields;
            const proRatePercent = common_1.utils.divideByOrZero(eligibleExposureLessConcentrationLimitKickouts, totalTopTwoEligibleLessKickout);
            const proRateReductionAmount = proRatePercent * overageKickoutAmount;
            kickouts[finleyReceivableId] = proRateReductionAmount;
        }
    }
    // *** Manage concentration calculation ***
    const concentrationAmount = totalTopTwoEligibleLessKickout;
    const concentration = common_1.utils.divideByOrZero(concentrationAmount, totalMerchandiseEligibleLessKickout);
    return {
        kickouts,
        calculations: {
            concentrationLimit: valueConfig.limit,
            concentrationAmount,
            concentration,
        },
    };
};
exports.concentrationLimitPointerMethods = [
    {
        method: 'okinusBastion1.topSellerRetailerOfNonHvacContracts',
        pointerImplementation: topSellerRetailerOfNonHvacContracts,
    },
    {
        method: 'okinusBastion1.topTwoSellerRetailerOfNonHvacContracts',
        pointerImplementation: topTwoSellerRetailerOfNonHvacContracts,
    },
];
//# sourceMappingURL=concentrationLimitPointerMethods.js.map